import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { showError } from "@/lib/errors";
import { Loader2, Tag, Users, Building2, Crown, UserCheck, CheckCircle2, Trophy } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  athlete:    { label: "Athlète",    Icon: Users,      endpoint: "/athletes" },
  coach:      { label: "Coach",      Icon: UserCheck,  endpoint: "/coaches" },
  club:       { label: "Club",       Icon: Building2,  endpoint: "/clubs" },
  federation: { label: "Fédération", Icon: Crown,      endpoint: "/federations" },
  competition:{ label: "Compétition",Icon: Trophy,     endpoint: "/competitions" },
};

/**
 * Popover réutilisable pour taguer un item Curator avec une entité (athlète/coach/club/fédé).
 * - Crée une CuratorAssociation via POST /api/curator/items/{itemId}/associations
 * - Notifie automatiquement l'owner cible (côté backend)
 *
 * Props :
 *  - itemId    : id du CuratorItem
 *  - existing  : liste des associations déjà présentes (pour éviter les doublons + indicateur visuel)
 *  - onAdded   : callback(updatedItem) après ajout réussi
 *  - size      : "sm" (défaut) | "icon"
 *  - variant   : variant du Button (défaut: "outline")
 *  - label     : texte du bouton (défaut: "Tag entité")
 */
export function TagEntityPopover({ itemId, existing = [], onAdded, size = "sm", variant = "outline", label = "Tag entité", target = "curator" }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("athlete");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const debounceRef = useRef(null);

  // Recherche debounced
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const meta = KIND_META[kind];
        const q = query.trim();
        const url = q ? `${meta.endpoint}?q=${encodeURIComponent(q)}&limit=8` : `${meta.endpoint}?limit=8`;
        const { data } = await api.get(url);
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        setResults(items.slice(0, 12));
      } catch (e) {
        setResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, kind, open]);

  const existingKey = (a) => {
    // Curator items have associations [{entity_type, entity_id}].
    // User articles have related_<kind>_ids[].
    if (a && a.entity_type && a.entity_id) return `${a.entity_type}:${a.entity_id}`;
    return null;
  };
  const existingSet = new Set((existing || []).map(existingKey).filter(Boolean));

  const handlePick = async (entity) => {
    const key = `${kind}:${entity.id}`;
    if (existingSet.has(key)) {
      toast.info("Cette entité est déjà taguée.");
      return;
    }
    setBusyId(entity.id);
    try {
      // V9.13 — route différente pour articles contributeurs vs Curator items.
      const url = target === "user_article"
        ? `/contrib/articles/${itemId}/tag`
        : `/curator/items/${itemId}/associations`;
      const body = target === "user_article"
        ? { entity_type: kind, entity_id: entity.id }
        : { entity_type: kind, entity_id: entity.id, placements: ["articles"], notify_owner: true };
      const { data } = await api.post(url, body);
      toast.success(`« ${entity.full_name || entity.name} » associé·e.`);
      onAdded?.(data);
      setOpen(false);
      setQuery("");
    } catch (e) {
      showError(e, { fallback: "Échec de l'ajout." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          data-testid={`tag-entity-trigger-${itemId}`}
          title="Tag entité"
          aria-label="Tag entité"
        >
          {size === "icon" ? (
            typeof label === "string" ? <Tag className="h-3.5 w-3.5" /> : label
          ) : (
            <>
              <Tag className="h-3.5 w-3.5 mr-1" />
              {label}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-3"
        align="end"
        onClick={(e) => e.stopPropagation()}
        data-testid={`tag-entity-popover-${itemId}`}
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Type d'entité</p>
            <div className="grid grid-cols-4 gap-1" data-testid="tag-entity-kinds">
              {Object.entries(KIND_META).map(([k, m]) => {
                const Icon = m.Icon;
                const active = kind === k;
                return (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded-md border text-[10px] transition-colors ${
                      active
                        ? "bg-[hsl(var(--aito-lagoon)/0.15)] border-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))] font-medium"
                        : "border-border hover:bg-muted"
                    }`}
                    data-testid={`tag-entity-kind-${k}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Rechercher un·e ${KIND_META[kind].label.toLowerCase()}…`}
            className="h-8 text-sm"
            data-testid="tag-entity-search"
            autoFocus
          />
          <ScrollArea className="max-h-[240px]" data-testid="tag-entity-results">
            {searching ? (
              <div className="flex items-center justify-center py-4 text-xs text-muted-foreground gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Recherche…
              </div>
            ) : results.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground italic">
                {query ? "Aucun résultat." : `Tapez pour rechercher un·e ${KIND_META[kind].label.toLowerCase()}.`}
              </div>
            ) : (
              <ul className="space-y-1">
                {results.map((r) => {
                  const name = r.full_name || r.name || r.title || "—";
                  const key = `${kind}:${r.id}`;
                  const already = existingSet.has(key);
                  return (
                    <li key={r.id}>
                      <button
                        onClick={() => !already && handlePick(r)}
                        disabled={already || busyId === r.id}
                        className={`w-full text-left flex items-center gap-2 p-1.5 rounded-md text-sm transition-colors ${
                          already ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
                        }`}
                        data-testid={`tag-entity-result-${r.id}`}
                      >
                        <span className="flex-1 truncate">{name}</span>
                        {r.discipline && (
                          <Badge variant="outline" className="text-[9px] capitalize">{String(r.discipline).slice(0, 14)}</Badge>
                        )}
                        {busyId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                        ) : already ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
          <p className="text-[10px] text-muted-foreground leading-snug">
            Astuce : le propriétaire de la fiche cible recevra une notification pour accepter, refuser ou demander correction.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
